import { useEffect, useRef, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const IMAGE_WIDTH = 1920;
const IMAGE_HEIGHT = 1080;
const THUMBNAIL_WIDTH = 320;
const THUMBNAIL_HEIGHT = 180;
const ASPECT_RATIO_TOLERANCE = 0.01;
const IMAGE_ACCEPT = "image/*,.heic,.heif,image/heic,image/heif";

const isHeicFile = (file) => {
  const filename = file.name.toLowerCase();
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    filename.endsWith(".heic") ||
    filename.endsWith(".heif")
  );
};

const isImageFile = (file) => {
  return file.type.startsWith("image/") || isHeicFile(file);
};

const getFileExtension = (fileType) => {
  if (fileType === "image/png") {
    return "png";
  }
  if (fileType === "image/webp") {
    return "webp";
  }
  return "jpg";
};

const getOutputType = (fileType) => {
  if (fileType === "image/png" || fileType === "image/webp") {
    return fileType;
  }
  return "image/jpeg";
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unable to read the selected image."));
    };

    image.src = url;
  });

const canvasToBlob = (canvas, fileType) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Unable to resize the selected image."));
          return;
        }
        resolve(blob);
      },
      fileType,
      0.92,
    );
  });

const convertHeicToJpeg = async (file) => {
  const { default: heic2any } = await import("heic2any");
  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.92,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const filename = `${file.name.replace(/\.[^/.]+$/, "")}.jpg`;

  return new File([blob], filename, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const resizeImageFile = async (
  file,
  width = IMAGE_WIDTH,
  height = IMAGE_HEIGHT,
) => {
  if (width <= 0 || height <= 0) {
    throw new Error("Image resize dimensions must be greater than zero.");
  }

  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Unable to resize the selected image.");
  }

  const targetAspectRatio = width / height;
  const sourceAspectRatio = image.width / image.height;
  const shouldCrop =
    Math.abs(sourceAspectRatio - targetAspectRatio) > ASPECT_RATIO_TOLERANCE;

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = image.width;
  let sourceHeight = image.height;

  if (shouldCrop) {
    if (sourceAspectRatio > targetAspectRatio) {
      sourceWidth = Math.floor(image.height * targetAspectRatio);
      sourceX = Math.floor((image.width - sourceWidth) / 2);
    } else {
      sourceHeight = Math.floor(image.width / targetAspectRatio);
      sourceY = Math.floor((image.height - sourceHeight) / 2);
    }
  }

  canvas.width = width;
  canvas.height = height;

  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    width,
    height,
  );

  const outputType = getOutputType(file.type);
  const blob = await canvasToBlob(canvas, outputType);

  if (blob.size > MAX_IMAGE_BYTES) {
    throw new Error("Resized image file size must be 2 MB or less.");
  }

  const filename = `${file.name.replace(/\.[^/.]+$/, "")}-${width}x${height}.${getFileExtension(outputType)}`;

  return new File([blob], filename, {
    type: outputType,
    lastModified: Date.now(),
  });
};

const ImageSelectDialog = ({
  open,
  onClose,
  onSelect,
  title = "Select Image",
  width = IMAGE_WIDTH,
  height = IMAGE_HEIGHT,
  thumbnailWidth = THUMBNAIL_WIDTH,
  thumbnailHeight = THUMBNAIL_HEIGHT,
}) => {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [description, setDescription] = useState("");

  const handleChangeDescription = (event) => {
    setDescription(event.target.value);
  };

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setPreviewUrl("");
      setError("");
      setProcessing(false);
      setDescription("");
    }
  }, [open]);

  useEffect(() => {
    if (!selectedFile) {
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setError("");
    setSelectedFile(null);

    if (!file) {
      return;
    }

    if (!isImageFile(file)) {
      setError("Please select an image file.");
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setError("Image file size must be 2 MB or less.");
      return;
    }

    try {
      setProcessing(true);
      const normalizedFile = isHeicFile(file) ? await convertHeicToJpeg(file) : file;
      setProcessing(false);

      if (normalizedFile.size > MAX_IMAGE_BYTES) {
        setError("Converted image file size must be 2 MB or less.");
        return;
      }

      setSelectedFile(normalizedFile);
    } catch (err) {
      setProcessing(false);
      setError("Unable to convert the selected Apple image format.");
    }
  };

  const handleSelect = async () => {
    if (!selectedFile) {
      setError("Please select an image file.");
      return;
    }

    try {
      setProcessing(true);
      const resizedFile = await resizeImageFile(selectedFile, width, height);
      const thumbnailFile = await resizeImageFile(
        selectedFile,
        thumbnailWidth,
        thumbnailHeight,
      );
      setProcessing(false);

      if (onSelect) {
        onSelect(resizedFile, thumbnailFile, selectedFile, description);
      }
    } catch (err) {
      setProcessing(false);
      setError(err.message);
    }
  };

  const handleClose = () => {
    if (!processing && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an image file up to 2 MB. Images already at a 16:9 ratio will
          be scaled to {width} x {height}. Other images will be cropped and
          scaled to {width} x {height}. The image will be reviewed and approved
          by the administrator before it is displayed to students.
        </Alert>

        <Button variant="contained" component="label">
          Choose Image
          <input
            ref={inputRef}
            type="file"
            hidden
            accept={IMAGE_ACCEPT}
            onChange={handleFileChange}
          />
        </Button>

        {selectedFile && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {selectedFile.name}
          </Typography>
        )}

        {previewUrl && (
          <Box
            component="img"
            src={previewUrl}
            alt="Selected preview"
            sx={{
              display: "block",
              width: 160,
              maxHeight: 160,
              objectFit: "contain",
              mt: 2,
              border: "1px solid #ddd",
            }}
          />
        )}

        <InputLabel sx={{ mt: 1 }}>Description:</InputLabel>
        <TextField
          margin="dense"
          type="text"
          fullWidth
          size="small"
          onChange={handleChangeDescription}
          placeholder="Required Field"
          value={description}
        />
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={processing} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedFile || processing}
          onClick={handleSelect}
        >
          {processing ? "Processing..." : "Select"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageSelectDialog;
